import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Collapse,
  TextField,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import {
  CustomButton,
  CustomSectionTitle,
  CustomModal,
  CustomLoading,
} from "@ippon/shared-components";
import {
  fetchEmailDomains,
  createEmailDomain,
  deleteEmailDomain,
  verifyEmailDomain,
  EmailDomain,
  DomainVerificationStatus,
} from "@/store/emailDomainSlice";
import { getClientID } from "@/utils/authUtils";
import { useCan } from "@/hooks/useCan";

// 全設定が完了しているか判定
const isEmailDomainComplete = (domain: EmailDomain): boolean =>
  domain.spf_status === "valid" &&
  domain.dkim_status === "valid" &&
  domain.dmarc_status === "valid" &&
  domain.ses_identity_status === "success";

// 完了ステータスバッジ
const CompletionStatusBadge: React.FC<{ domain: EmailDomain }> = ({
  domain,
}) => {
  const complete = isEmailDomainComplete(domain);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: "7px",
        py: "2px",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: 700,
        whiteSpace: "nowrap",
        ...(complete
          ? {
              backgroundColor: "#edffee",
              color: "#188d1c",
              border: "1px solid #a9deab",
            }
          : {
              backgroundColor: "#fff8e1",
              color: "#e65100",
              border: "1px solid #ffcc80",
            }),
      }}
    >
      {complete ? "完了" : "未完了"}
    </Box>
  );
};

// DNS検証ステータスバッジ
const statusStyles: Record<DomainVerificationStatus, React.CSSProperties> = {
  valid: {
    backgroundColor: "#edffee",
    color: "#188d1c",
    border: "1px solid #a9deab",
  },
  invalid: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    border: "1px solid #ef9a9a",
  },
  unverified: {
    backgroundColor: "#f5f5f5",
    color: "#989898",
    border: "1px solid #d9d9d9",
  },
};

const statusLabels: Record<DomainVerificationStatus, string> = {
  valid: "正常",
  invalid: "異常",
  unverified: "未検証",
};

const DnsStatusBadge: React.FC<{
  status: DomainVerificationStatus;
  label: string;
}> = ({ status, label }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: "7px",
      py: "2px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: 700,
      whiteSpace: "nowrap",
      flexShrink: 0,
      ...statusStyles[status],
    }}
  >
    {label}：{statusLabels[status]}
  </Box>
);

// コピー可能なセル（テキストとボタンを別グリッドセルとして返す）
const CopyableCell: React.FC<{ text: string; copyText?: string }> = ({ text, copyText }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(copyText ?? text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <>
      <Tooltip title={copied ? "コピーしました" : "コピー"} placement="top">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ p: "2px", color: copied ? "#188d1c" : "#989898" }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 13 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 13 }} />
          )}
        </IconButton>
      </Tooltip>
      <Typography
        sx={{
          fontSize: 12,
          color: "#3e3e3e",
          wordBreak: "break-all",
          fontFamily: "monospace",
          alignSelf: "center",
        }}
      >
        {text}
      </Typography>
    </>
  );
};

// DNSレコード展開パネル
const DnsRecordsPanel: React.FC<{ emailDomain: EmailDomain }> = ({
  emailDomain,
}) => {
  const { domain, ses_domain_token, ses_dkim_tokens } = emailDomain;
  const dkimTokens = ses_dkim_tokens ? ses_dkim_tokens.split(",") : [];
  const mailFromDomain = `ippon-mail.${domain}`;

  // name: 表示用（フルホスト名）, copyName: コピー用（サブドメイン部分のみ）
  const records: { type: string; name: string; copyName: string; value: string }[] = [];

  if (ses_domain_token) {
    records.push({
      type: "TXT",
      name: `_amazonses.${domain}`,
      copyName: "_amazonses",
      value: ses_domain_token,
    });
  }

  dkimTokens.forEach((token) => {
    records.push({
      type: "CNAME",
      name: `${token}._domainkey.${domain}`,
      copyName: `${token}._domainkey`,
      value: `${token}.dkim.amazonses.com`,
    });
  });

  // ドメイン SPF
  records.push({
    type: "TXT",
    name: domain,
    copyName: "@",
    value: "v=spf1 include:amazonses.com ~all",
  });

  // カスタム MAIL FROM ドメイン (ippon-mail.<domain>)
  records.push({
    type: "MX",
    name: mailFromDomain,
    copyName: "ippon-mail",
    value: "10 feedback-smtp.ap-northeast-1.amazonses.com",
  });
  records.push({
    type: "TXT",
    name: mailFromDomain,
    copyName: "ippon-mail",
    value: "v=spf1 include:amazonses.com ~all",
  });

  // DMARC
  records.push({
    type: "TXT",
    name: `_dmarc.${domain}`,
    copyName: "_dmarc",
    value: `v=DMARC1; p=quarantine; rua=mailto:dmarc-reports@${domain}; ruf=mailto:dmarc-reports@${domain}; adkim=r; aspf=r; pct=100`,
  });

  return (
    <Box
      sx={{
        px: "12px",
        py: "10px",
        backgroundColor: "#fff",
        borderTop: "1px solid rgba(224, 224, 224, 1)",
      }}
    >
      <Typography
        sx={{ fontSize: 12, fontWeight: 700, color: "#3e3e3e", mb: 1 }}
      >
        設定が必要なDNSレコード
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "60px 24px 1fr 24px 1fr",
          gap: "4px 8px",
          fontSize: 12,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#989898" }}>
          種別
        </Typography>
        <Box />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#989898" }}>
          ホスト名
        </Typography>
        <Box />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#989898" }}>
          値
        </Typography>
        {records.map((rec, i) => (
          <React.Fragment key={i}>
            <Typography
              sx={{ fontSize: 12, color: "#3e3e3e", fontWeight: 600 }}
            >
              {rec.type}
            </Typography>
            <CopyableCell text={rec.name} copyText={rec.copyName} />
            <CopyableCell text={rec.value} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

const EmailDomainSetting: React.FC = () => {
  const can = useCan();
  const canEditDomain = can("client:edit");
  const dispatch = useDispatch<AppDispatch>();
  const { domains, verifying } = useSelector(
    (state: RootState) => state.emailDomain
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [newDomain, setNewDomain] = useState<string>("");
  const [domainError, setDomainError] = useState<string>("");
  const [addingDomain, setAddingDomain] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [deletingDomainId, setDeletingDomainId] = useState<string | null>(null);
  const [expandedDomainIds, setExpandedDomainIds] = useState<
    Record<string, boolean>
  >({});

  // メールドメイン一覧の取得
  useEffect(() => {
    const clientId = getClientID();
    if (clientId) {
      dispatch(fetchEmailDomains(clientId));
    }
  }, [dispatch]);

  // ── メールドメイン追加 ──
  const handleAddDomain = async () => {
    if (!canEditDomain) return;
    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    if (!newDomain.trim()) {
      setDomainError("ドメインを入力してください");
      return;
    }
    if (!domainRegex.test(newDomain.trim())) {
      setDomainError("ドメインの形式が正しくありません（例: example.com）");
      return;
    }
    const clientId = getClientID();
    if (!clientId) return;

    setAddingDomain(true);
    setDomainError("");
    try {
      await dispatch(
        createEmailDomain({
          clientId,
          domain: newDomain.trim(),
        })
      ).unwrap();
      setNewDomain("");
    } catch (error: any) {
      setDomainError(error?.message || "ドメインの追加に失敗しました");
    } finally {
      setAddingDomain(false);
    }
  };

  // ── 削除 ──
  const handleOpenDeleteModal = (domainId: string) => {
    if (!canEditDomain) return;
    setDeletingDomainId(domainId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!canEditDomain) return;
    const clientId = getClientID();
    if (!clientId || !deletingDomainId) return;

    await dispatch(deleteEmailDomain({ clientId, domainId: deletingDomainId }));
    setDeleteConfirmOpen(false);
    setDeletingDomainId(null);
  };

  const handleCloseDeleteModal = () => {
    setDeleteConfirmOpen(false);
    setDeletingDomainId(null);
  };

  // ── DNS設定展開トグル ──
  const handleToggleExpand = (domainId: string) => {
    setExpandedDomainIds((prev) => ({
      ...prev,
      [domainId]: !prev[domainId],
    }));
  };

  // ── DNS検証 ──
  const handleVerify = async (domainId: string) => {
    if (!canEditDomain) return;
    const clientId = getClientID();
    if (!clientId) return;
    await dispatch(verifyEmailDomain({ clientId, domainId }));
  };

  const deletingDomain = domains.data.find((d) => d.id === deletingDomainId);

  const thTextStyle = {
    fontWeight: 600,
    fontSize: "14px",
    color: "#3e3e3e",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textAlign: "left" as const,
  };

  // テーブルヘッダーセルのスタイル
  const thStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    height: "100%",
    px: "12px",
    overflow: "hidden",
    flexShrink: 0,
    position: "relative" as const,
    "&::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: "6px",
      bottom: "6px",
      width: "1px",
      backgroundColor: "rgba(224, 224, 224, 1)",
    },
  };
  const thLastStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    height: "100%",
    px: "12px",
    overflow: "hidden",
    flexShrink: 0,
  };

  return (
    <Box pb={3} pt={{ xs: 2, sm: 3 }}>
      <CustomSectionTitle title="メールドメイン設定" size="large" />

      <Typography
        variant="body2"
        sx={{
          mb: 1,
          mt: 2,
          color: "#666",
          fontSize: { xs: 12, sm: 14 },
          lineHeight: 1.6,
        }}
      >
        メール送信用のドメインを登録し、DNS設定を行います。
        <br />
        各ドメインに必要なDNSレコード（SPF・DKIM・DMARC）を設定してください。反映には最大72時間かかります。
        <br />
        「検証」ボタンを押してステータスが「完了」になれば設定完了です。
      </Typography>

      {/* ドメイン追加フォーム（未登録時のみ表示） */}
      {domains.data.length === 0 && canEditDomain && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography
            sx={{
              fontSize: { xs: 12, sm: 13 },
              fontWeight: "bold",
              color: "#3e3e3e",
              mb: 1.5,
            }}
          >
            ドメインを追加
          </Typography>
          <Box sx={{ width: "100%", maxWidth: "600px" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "flex-end",
              }}
            >
              <TextField
                placeholder="例: example.com"
                value={newDomain}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewDomain(e.target.value);
                  setDomainError("");
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter") handleAddDomain();
                }}
                error={!!domainError}
                size="small"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  flex: { sm: 1 },
                  minWidth: 0,
                  "& .MuiOutlinedInput-root": {
                    height: "34px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    backgroundColor: "#fff",
                    "& fieldset": { borderColor: "#D9D9D9" },
                    "&:hover fieldset": { borderColor: "#D9D9D9" },
                  },
                  "& input": { fontSize: "12px", padding: "8px 12px" },
                }}
              />

              <CustomButton
                label="追加"
                onClick={handleAddDomain}
                disabled={addingDomain || !newDomain.trim()}
                isLoading={addingDomain}
              />
            </Box>
            {domainError && (
              <Typography sx={{ fontSize: 12, color: "#c62828", mt: 0.75 }}>
                {domainError}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* ドメイン一覧 */}
      {domains.loading ? (
        <CustomLoading
          loading={domains.loading}
          fullScreen={false}
          size="medium"
          variant="spinner"
        />
      ) : domains.data.length === 0 ? (
        <Box
          sx={{
            mt: 2,
            py: 3,
            textAlign: "center",
            border: "1px solid rgba(224, 224, 224, 1)",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <Typography
            sx={{ fontSize: { xs: 12, sm: 13 }, color: "#989898" }}
          >
            登録されているドメインはありません
          </Typography>
        </Box>
      ) : isMobile ? (
        /* モバイル: カードレイアウト */
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
          {domains.data.map((emailDomain: EmailDomain) => {
            const isVerifying = !!verifying[emailDomain.id];
            const isExpanded = !!expandedDomainIds[emailDomain.id];
            return (
              <Box
                key={emailDomain.id}
                sx={{
                  border: "1px solid rgba(224, 224, 224, 1)",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                }}
              >
                {/* カードヘッダー */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 1.5,
                    gap: 1,
                  }}
                >
                  <IconButton
                    onClick={() => handleToggleExpand(emailDomain.id)}
                    size="small"
                    sx={{
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "all 0.2s",
                      color: isExpanded ? "#0E9DBC" : "#3e3e3e",
                      backgroundColor: isExpanded ? "#DBF8FF" : "transparent",
                      width: 26,
                      height: 26,
                      borderRadius: "8px",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "#DBF8FF",
                        "& svg": { color: "#0E9DBC" },
                      },
                    }}
                  >
                    <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: "bold",
                      color: "#3e3e3e",
                      wordBreak: "break-all",
                      flex: 1,
                    }}
                  >
                    {emailDomain.domain}
                  </Typography>
                  <CompletionStatusBadge domain={emailDomain} />
                </Box>

                {/* DNS状況 */}
                <Box
                  sx={{
                    px: 1.5,
                    pb: 1,
                    display: "flex",
                    flexWrap: "nowrap",
                    gap: 0.75,
                    overflowX: "auto",
                  }}
                >
                  <DnsStatusBadge status={emailDomain.spf_status} label="SPF" />
                  <DnsStatusBadge status={emailDomain.dkim_status} label="DKIM" />
                  <DnsStatusBadge status={emailDomain.dmarc_status} label="DMARC" />
                </Box>

                {/* 最終検証日時 */}
                <Box sx={{ px: 1.5, pb: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "#3e3e3e" }}>
                    最終検証：
                    {emailDomain.last_verified_at
                      ? new Date(emailDomain.last_verified_at).toLocaleString("ja-JP", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </Typography>
                </Box>

                {/* 操作ボタン */}
                <Box
                  sx={{
                    px: 1.5,
                    pb: 1.5,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  {canEditDomain && (
                    <>
                      <CustomButton
                        label={isVerifying ? "検証中..." : "検証"}
                        onClick={() => handleVerify(emailDomain.id)}
                        disabled={isVerifying}
                        isLoading={isVerifying}
                        sx={{ height: "36px !important", minWidth: "72px !important", padding: "0 12px !important" }}
                      />
                      <CustomButton
                        label="削除"
                        onClick={() => handleOpenDeleteModal(emailDomain.id)}
                        buttonCategory="delete"
                        sx={{ height: "36px !important", minWidth: "72px !important", padding: "0 12px !important" }}
                      />
                    </>
                  )}
                  {!canEditDomain && (
                    <Typography sx={{ fontSize: "14px" }}>-</Typography>
                  )}
                </Box>

                {/* DNS展開パネル */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <DnsRecordsPanel emailDomain={emailDomain} />
                </Collapse>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: "8px",
            overflowX: "auto",
            border: "1px solid rgba(224, 224, 224, 1)",
          }}
        >
          <Box sx={{ minWidth: 940 }}>
          {/* テーブルヘッダー */}
          <Box
            sx={{
              display: "flex",
              backgroundColor: "#F5F5F5",
              height: "36px",
              alignItems: "center",
              borderBottom: "1px solid rgba(224, 224, 224, 1)",
              minWidth: "fit-content",
            }}
          >
            {/* 展開アイコン列プレースホルダー */}
            <Box sx={{ width: 44, flexShrink: 0 }} />
            <Box sx={{ ...thStyle, flex: 1, minWidth: 150 }}>
              <Typography sx={thTextStyle}>ドメイン</Typography>
            </Box>
            <Box sx={{ ...thStyle, width: 100 }}>
              <Typography sx={thTextStyle}>ステータス</Typography>
            </Box>
            <Box sx={{ ...thStyle, width: 360 }}>
              <Typography sx={thTextStyle}>DNS設定状況</Typography>
            </Box>
            <Box sx={{ ...thStyle, width: 130 }}>
              <Typography sx={thTextStyle}>最終検証日時</Typography>
            </Box>
            <Box
              sx={{
                ...thLastStyle,
                flex: 1,
                minWidth: 180,
              }}
            >
              <Typography sx={thTextStyle}>操作</Typography>
            </Box>
          </Box>

          {/* テーブルボディ */}
          <Box sx={{ backgroundColor: "#fff" }}>
            {domains.data.map((emailDomain: EmailDomain, index: number) => {
              const isVerifying = !!verifying[emailDomain.id];
              const isLast = index === domains.data.length - 1;
              const isExpanded = !!expandedDomainIds[emailDomain.id];

              return (
                <Box
                  key={emailDomain.id}
                  sx={{
                    borderBottom: isLast
                      ? "none"
                      : "1px solid rgba(224, 224, 224, 1)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: "52px",
                      minWidth: "fit-content",
                    }}
                  >
                    {/* 展開アイコン */}
                    <Box
                      sx={{
                        width: 44,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconButton
                        onClick={() => handleToggleExpand(emailDomain.id)}
                        size="small"
                        sx={{
                          transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                          transition: "all 0.2s",
                          color: isExpanded ? "#0E9DBC" : "#3e3e3e",
                          backgroundColor: isExpanded ? "#DBF8FF" : "transparent",
                          width: 26,
                          height: 26,
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor: "#DBF8FF",
                            "& svg": { color: "#0E9DBC" },
                          },
                        }}
                      >
                        <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>

                    {/* ドメイン名 */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 150,
                        flexShrink: 0,
                        px: "12px",
                        py: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: "bold",
                          color: "#3e3e3e",
                          wordBreak: "break-all",
                        }}
                      >
                        {emailDomain.domain}
                      </Typography>
                    </Box>

                    {/* ステータスバッジ */}
                    <Box
                      sx={{
                        width: 100,
                        flexShrink: 0,
                        px: "12px",
                        py: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <CompletionStatusBadge domain={emailDomain} />
                    </Box>

                    {/* DNS設定状況バッジ */}
                    <Box
                      sx={{
                        width: 360,
                        flexShrink: 0,
                        px: "12px",
                        py: "8px",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        alignItems: "center",
                        justifyContent: "flex-start",
                      }}
                    >
                      <DnsStatusBadge
                        status={emailDomain.spf_status}
                        label="SPF"
                      />
                      <DnsStatusBadge
                        status={emailDomain.dkim_status}
                        label="DKIM"
                      />
                      <DnsStatusBadge
                        status={emailDomain.dmarc_status}
                        label="DMARC"
                      />
                    </Box>

                    {/* 最終検証日時 */}
                    <Box
                      sx={{
                        width: 130,
                        flexShrink: 0,
                        px: "12px",
                        py: "8px",
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 14, color: "#3e3e3e" }}
                      >
                        {emailDomain.last_verified_at
                          ? new Date(
                              emailDomain.last_verified_at
                            ).toLocaleString("ja-JP", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </Typography>
                    </Box>

                    {/* 操作列：検証・削除 */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 180,
                        flexShrink: 0,
                        py: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 1,
                      }}
                    >
                      {canEditDomain && (
                        <>
                          <CustomButton
                            label={isVerifying ? "検証中..." : "検証"}
                            onClick={() => handleVerify(emailDomain.id)}
                            disabled={isVerifying}
                            isLoading={isVerifying}
                            sx={{
                              height: "36px !important",
                              width: "72px !important",
                              minWidth: "72px !important",
                              padding: "0 !important",
                            }}
                          />
                          <CustomButton
                            label="削除"
                            onClick={() => handleOpenDeleteModal(emailDomain.id)}
                            buttonCategory="delete"
                            sx={{
                              height: "36px !important",
                              width: "72px !important",
                              minWidth: "72px !important",
                              padding: "0 !important",
                            }}
                          />
                        </>
                      )}
                      {!canEditDomain && (
                        <Typography sx={{ fontSize: "14px" }}>-</Typography>
                      )}
                    </Box>
                  </Box>

                  {/* DNS設定展開パネル */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <DnsRecordsPanel emailDomain={emailDomain} />
                  </Collapse>
                </Box>
              );
            })}
          </Box>
          </Box>
        </Box>
      )}

      {/* ドメイン削除確認モーダル */}
      <CustomModal
        openModal={deleteConfirmOpen && canEditDomain}
        handleCloseModal={handleCloseDeleteModal}
        title="ドメインの削除"
        modalType="otherModal"
        bodyText={`以下のドメインを\n削除してよろしいですか？\n${deletingDomain?.domain ?? ""}`}
      >
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <CustomButton
            label="戻る"
            onClick={handleCloseDeleteModal}
            buttonCategory="cancel"
          />
          <CustomButton
            label="削除する"
            onClick={handleConfirmDelete}
            buttonCategory="deleteModal"
          />
        </Box>
      </CustomModal>
    </Box>
  );
};

export default EmailDomainSetting;
